import clsx from 'clsx';
import type { NarrativeBarShareMode } from '@/components/layout/NarrativeBar';

export interface SandboxBannerProps {
  shareMode: NarrativeBarShareMode;
  /**
   * True when `Object.keys(sandbox.overrides).length > 0`. Banner is
   * suppressed when false — matches the spec §10 contract that the
   * banner only surfaces "when the override map is non-empty."
   */
  hasOverrides: boolean;
  /**
   * Click handler for the [Sign up] button (`'external-anon'` mode).
   * Typically navigates to `/signup`.
   */
  onSignupClick?: () => void;
  /**
   * Click handler for the [Fork] button (`'internal-recipient'` mode).
   * Typically calls `useForkShare().mutate(shareId)`.
   */
  onForkClick?: () => void;
  /**
   * Click handler for the [Reset] button (`'external-authed'` mode).
   * Typically calls `sandbox.clearOverrides()`.
   */
  onResetClick?: () => void;
  className?: string;
}

interface ModeConfig {
  copy: string;
  buttonLabel: string;
  buttonHandlerKey: 'onSignupClick' | 'onForkClick' | 'onResetClick';
}

const MODE_CONFIG: Record<
  Exclude<NarrativeBarShareMode, 'owner'>,
  ModeConfig
> = {
  'external-anon': {
    copy: "You've made local changes — sign up to save",
    buttonLabel: 'Sign up',
    buttonHandlerKey: 'onSignupClick',
  },
  'external-authed': {
    copy: "You've made local changes — they'll be lost on refresh",
    buttonLabel: 'Reset',
    buttonHandlerKey: 'onResetClick',
  },
  'internal-recipient': {
    copy: "You've made local changes — fork to make them persistent",
    buttonLabel: 'Fork',
    buttonHandlerKey: 'onForkClick',
  },
};

/**
 * `external-sharing` Phase 5 Session 5.4 (2026-05-09).
 *
 * Per-viewer-mode banner that surfaces when the recipient has staged
 * sandbox edits but hasn't persisted them. The banner is intentionally
 * unobtrusive — `bg-warn-dim border border-warn/15 text-warn` (matches
 * `<ShareForkBanner>`'s pattern) — and renders ABOVE the Model grid.
 *
 * Mode matrix (spec §10 + Phase 5 Session 5.4 implementation notes):
 *   - `'external-anon'`: "Sign up to save" + Sign-up CTA → `/signup`.
 *   - `'external-authed'`: "they'll be lost on refresh" + Reset CTA →
 *     `sandbox.clearOverrides()`. Spec §10 is silent on this mode's
 *     copy — the Reset framing leans on the v1 user research that
 *     authed non-recipient viewers (forwardees / colleagues) don't need
 *     a stronger CTA than "your changes won't persist".
 *   - `'internal-recipient'`: "fork to make them persistent" + Fork
 *     CTA → `useForkShare()`.
 *   - `'owner'`: NOT rendered. Owners previewing via `?preview=true`
 *     don't need a banner — their edits are demonstrative, not
 *     intended to persist.
 *
 * Suppressed (returns null) when `hasOverrides === false` — the banner
 * only surfaces while the user has staged at least one edit.
 */
export function SandboxBanner({
  shareMode,
  hasOverrides,
  onSignupClick,
  onForkClick,
  onResetClick,
  className,
}: SandboxBannerProps) {
  if (!hasOverrides) return null;
  if (shareMode === 'owner') return null;

  const config = MODE_CONFIG[shareMode];
  const handler =
    config.buttonHandlerKey === 'onSignupClick'
      ? onSignupClick
      : config.buttonHandlerKey === 'onForkClick'
      ? onForkClick
      : onResetClick;

  return (
    <div
      data-testid="sandbox-banner"
      data-share-mode={shareMode}
      role="status"
      className={clsx(
        'flex items-center justify-between gap-3 px-4 py-2.5 border border-warn/15 rounded-[3px] bg-warn-dim text-warn',
        className,
      )}
    >
      <span className="text-xs font-mono leading-relaxed">{config.copy}</span>
      <button
        type="button"
        onClick={handler}
        disabled={!handler}
        data-testid="sandbox-banner-action"
        className="shrink-0 px-3 py-1.5 border border-warn/40 rounded-[2px] bg-warn/10 text-warn text-xs font-mono font-semibold tracking-[0.3px] uppercase hover:bg-warn/20 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {config.buttonLabel}
      </button>
    </div>
  );
}
